import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo'
import { faTag } from '@fortawesome/pro-solid-svg-icons/faTag'
import { faTags } from '@fortawesome/pro-solid-svg-icons/faTags'
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash'
import { ProfileSelectOption } from 'components/ui/forms/course/components/forms/RequiredProfilesInput'
import { ProfileBadge } from 'components/reusables/profileBadge'
import { AddCourseProfilesModal } from './AddCourseProfilesModal'
import styles from './styles.module.scss'

interface CourseProfilesCardProps {
  label?: string
  profiles: ProfileSelectOption[]
  excludedProfileIds: Set<string>
  restrictedProfilesCount?: number
  onChange: (profiles: ProfileSelectOption[]) => void
  disabled?: boolean
  error?: string
  translationPrefix?: string
  showLabel?: boolean
}

export const CourseProfilesCard = ({
  label,
  profiles,
  excludedProfileIds,
  restrictedProfilesCount = 0,
  onChange,
  disabled = false,
  error,
  translationPrefix = 'administration.courses.new.form.profilesField',
  showLabel = true,
}: CourseProfilesCardProps) => {
  const { t } = useTranslation()
  const prefix = translationPrefix
  const [showModal, setShowModal] = useState(false)

  const totalCount = profiles.length + restrictedProfilesCount
  const hasListContent = profiles.length > 0 || restrictedProfilesCount > 0

  const modalExcludedIds = useMemo(() => {
    const ids = new Set(excludedProfileIds)
    profiles.forEach((profile) => ids.add(String(profile.value)))
    return ids
  }, [excludedProfileIds, profiles])

  const handleAdd = (addedProfiles: ProfileSelectOption[]) => {
    onChange([...profiles, ...addedProfiles])
  }

  const handleRemove = (value: string | number) => {
    onChange(
      profiles.filter((profile) => String(profile.value) !== String(value))
    )
  }

  return (
    <div className={styles.cardSection}>
      {showLabel && label ? (
        <p className={styles.sectionLabel}>{label}</p>
      ) : null}

      <div className={styles.card}>
        {hasListContent && (
          <div className={styles.listScroll}>
            <div className={styles.listRows}>
              {profiles.map((profile) => (
                <div key={String(profile.value)} className={styles.listRow}>
                  <div className={styles.rowContent}>
                    <ProfileBadge name={profile.label} color={profile.color} />
                  </div>
                  <div className={styles.rowActions}>
                    <button
                      type="button"
                      className={styles.removeButton}
                      disabled={disabled}
                      onClick={() => handleRemove(profile.value)}
                      aria-label={t(`${prefix}.removeProfile`, {
                        defaultValue: 'Eliminar perfil',
                      })}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}

              {restrictedProfilesCount > 0 && (
                <div className={styles.listRow}>
                  <div className={styles.rowContent}>
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className={styles.restrictedIcon}
                    />
                    <span className={styles.restrictedText}>
                      {t(`${prefix}.restrictedProfiles`, {
                        count: restrictedProfilesCount,
                      })}
                    </span>
                  </div>
                  <div className={styles.rowActions}>
                    <span className={styles.removeButtonPlaceholder} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div
          className={
            hasListContent ? styles.cardFooter : styles.cardFooterCompact
          }
        >
          <span className={styles.countTag}>
            <FontAwesomeIcon icon={faTags} />
            {t(`${prefix}.count`, { count: totalCount })}
          </span>
          <button
            type="button"
            className={styles.addButton}
            disabled={disabled}
            onClick={() => setShowModal(true)}
          >
            <FontAwesomeIcon icon={faTag} />
            {t(`${prefix}.add`)}
          </button>
        </div>
      </div>

      {error && <p className={styles.error}>{error}</p>}

      {showModal && (
        <AddCourseProfilesModal
          excludedProfileIds={modalExcludedIds}
          onClose={() => setShowModal(false)}
          onAdd={handleAdd}
        />
      )}
    </div>
  )
}

export default CourseProfilesCard
